<script lang="ts">
  import type { BackgroundOption } from '@athena-lib/BackgroundSelector';
  import { ProductWizardContext, SizeHelper } from '@stickerapp-org/nomisma';
  import type { ProductItemDimensions } from '../types';

  type StickerPreviewRulerProps = {
    show: boolean;
    uniqueId: string;
    productItemDimensions?: ProductItemDimensions;
    backgroundOption: BackgroundOption;
  };

  let { show, uniqueId, productItemDimensions, backgroundOption }: StickerPreviewRulerProps =
    $props();

  let inlineStyleWidth = $state('');
  let inlineStyleHeight = $state('');

  const context = new ProductWizardContext();
  const sizeHelper = new SizeHelper(context.attrComputer, context.productItem);

  window.onresize = () => {
    requestAnimationFrame(updateDimensions);
  };

  $effect(() => {
    if (show) {
      updateDimensions();
    }
  });

  function updateDimensions() {
    const baseplateLayer = document.getElementById(`basePlateLayer-${uniqueId}`);
    if (!baseplateLayer) {
      return;
    }
    const boundClient = baseplateLayer.getBoundingClientRect();
    const checkeredContainer = document.getElementsByClassName(`checkered`)[0];
    const rulerHeight = 30 + boundClient.height * 0.02;
    if (checkeredContainer) {
      const margin = 13;
      inlineStyleWidth = `
      top: ${-boundClient.top + boundClient.y - margin}px;
      width: ${boundClient.width}px;
      height: ${rulerHeight}px;
      `;

      inlineStyleHeight = `
      left: ${-boundClient.left + boundClient.x - margin}px;
      height: ${boundClient.height}px;
      width: ${rulerHeight}px;
      `;
    } else {
      inlineStyleWidth = `top: ${boundClient.y - 60}px; left: ${boundClient.x}px; width: ${boundClient.width}px; height: ${rulerHeight}px;`;
      inlineStyleHeight = `left: ${boundClient.x - 60}px; top: ${boundClient.y}px; height: ${boundClient.height}px; width: ${rulerHeight}px;`;
    }
  }

  function displayableMeasure(valueMM: number | undefined): string {
    if (!productItemDimensions || !valueMM) {
      return '';
    }

    const value = productItemDimensions.imperialUnits
      ? sizeHelper.getSizeByMM(valueMM).inches + '"'
      : sizeHelper.getSizeByMM(valueMM).cm + ' cm';

    return `${value}`;
  }
</script>

{#if show}
  <div class="ruler width" style={inlineStyleWidth}>
    <div class={`left-line-container ${backgroundOption}`}>
      <div class={`line ${backgroundOption}`}></div>
    </div>
    <div class={`text ${backgroundOption}`}>
      {displayableMeasure(productItemDimensions?.widthMM)}
    </div>
    <div class={`right-line-container ${backgroundOption}`}>
      <div class={`line ${backgroundOption}`}></div>
    </div>
  </div>
  <div class="ruler height" style={inlineStyleHeight}>
    <div class={`top-line-container ${backgroundOption}`}>
      <div class={`line ${backgroundOption}`}></div>
    </div>
    <div class={`text ${backgroundOption}`}>
      {displayableMeasure(productItemDimensions?.heightMM)}
    </div>
    <div class={`bottom-line-container ${backgroundOption}`}>
      <div class={`line ${backgroundOption}`}></div>
    </div>
  </div>
{/if}

<style>
  .ruler {
    position: absolute;
    display: flex;
    z-index: 2;
    &.width {
      flex-direction: row;
      .left-line-container {
        width: 40%;
        display: flex;
        align-items: center;
        border-left: 1px solid;
        border-color: grey;
        &.dark {
          border-color: rgb(77, 77, 77);
        }
      }
      .right-line-container {
        width: 40%;
        display: flex;
        align-items: center;
        border-right: 1px solid;
        border-color: grey;
        &.dark {
          border-color: rgb(77, 77, 77);
        }
      }
      .line {
        height: 1px;
        background-color: grey;
        width: 100%;
        &.dark {
          background-color: rgb(77, 77, 77);
        }
      }
      .text {
        width: 20%;
        display: flex;
        align-items: center;
        justify-content: center;
        font-size: 18px;
        font-weight: bold;
        color: grey;
        &.dark {
          color: rgb(77, 77, 77);
        }
      }
    }
    &.height {
      flex-direction: column;
      align-items: center;
      .top-line-container {
        height: 30%;
        width: 100%;
        justify-content: center;
        display: flex;
        align-items: center;
        border-top: 1px solid;
        border-color: grey;
        &.dark {
          border-color: rgb(77, 77, 77);
        }
      }

      .bottom-line-container {
        height: 30%;
        width: 100%;
        justify-content: center;
        display: flex;
        align-items: center;
        border-bottom: 1px solid;
        border-color: grey;

        &.dark {
          border-color: rgb(77, 77, 77);
        }
      }
      .line {
        width: 1px;
        background-color: grey;
        height: 100%;
        &.dark {
          background-color: rgb(77, 77, 77);
        }
      }
      .text {
        height: 40%;
        width: 100px;
        display: flex;
        align-items: center;
        justify-content: center;
        font-size: 18px;
        font-weight: bold;
        color: grey;
        transform: rotate(-90deg);
        &.dark {
          color: rgb(77, 77, 77);
        }
      }
    }
    @media (max-width: 1024px) {
      display: none;
    }
  }
</style>
